/**
 * Ordered sidebar navigation routes shared by SideNav and permission-based landing.
 * Icons stay in SideNav; only route metadata lives here.
 *
 * Groups without `titleKey` render as top-level menu links (no section heading).
 */
export type NavItemDef = {
  to: string;
  labelKey: string;
  end?: boolean;
};

export type NavGroupDef = {
  /** When omitted, items render as main menu options without a group heading. */
  titleKey?: string;
  items: NavItemDef[];
};

export const NAV_GROUPS: NavGroupDef[] = [
  {
    titleKey: 'nav.groups.dashboard',
    items: [
      { to: '/dashboards/sg', labelKey: 'nav.sgDashboard' },
      { to: '/dashboards/operational', labelKey: 'nav.operationalDashboard' },
    ],
  },
  {
    titleKey: 'nav.groups.work',
    items: [
      { to: '/mail-room/requests', labelKey: 'nav.mailRoom' },
      { to: '/tasks/assign', labelKey: 'nav.assignTasks' },
      { to: '/tasks/case-details', labelKey: 'nav.caseDetails' },
      { to: '/tasks', labelKey: 'nav.tasksAndCases', end: true },
      { to: '/tasks/attending', labelKey: 'nav.attendingTasksAndCases' },
      { to: '/tasks/escalation', labelKey: 'nav.escalationTasks' },
      { to: '/record-room/completed-files', labelKey: 'nav.recordRoom' },
      { to: '/record-room/qr-scanning-log', labelKey: 'nav.qrScanningLog' },
    ],
  },
  {
    titleKey: 'nav.groups.legalKnowledgeArchive',
    items: [{ to: '/legal-knowledge/archive', labelKey: 'nav.legalKnowledge', end: true }],
  },
  {
    titleKey: 'nav.groups.administration',
    items: [
      { to: '/admin/intranet-users', labelKey: 'nav.allIntranetUsers' },
      { to: '/admin/user-categories', labelKey: 'nav.userCategories' },
      { to: '/admin/roles-permissions', labelKey: 'nav.rolesPermissions' },
      { to: '/admin/extranet-users', labelKey: 'nav.allExtranetUsers' },
    ],
  },
  {
    titleKey: 'nav.groups.workflowManagement',
    items: [{ to: '/workflow/templates', labelKey: 'nav.workflowTemplates' }],
  },
  {
    titleKey: 'nav.groups.legalKnowledgeArchiveManagement',
    items: [
      { to: '/legal-knowledge/archive/management/dashboard', labelKey: 'nav.dashboard' },
      { to: '/legal-knowledge/archive/management/outcomes', labelKey: 'nav.extractedCaseOutcomes' },
    ],
  },
  {
    titleKey: 'nav.groups.reports',
    items: [
      { to: '/reports/user-performance', labelKey: 'nav.userPerformanceReport' },
      { to: '/reports/case-tracking', labelKey: 'nav.caseTrackingReport' },
      { to: '/reports/workflow-performance', labelKey: 'nav.workflowPerformanceReport' },
    ],
  },
  {
    titleKey: 'nav.groups.auditLogs',
    items: [{ to: '/audit-logs', labelKey: 'nav.auditLogs' }],
  },
  {
    titleKey: 'nav.groups.metadata',
    items: [
      { to: '/metadata/subcategories', labelKey: 'nav.subcategory' },
      { to: '/metadata/courts', labelKey: 'nav.court' },
      { to: '/metadata/languages', labelKey: 'nav.language' },
      { to: '/metadata/countries', labelKey: 'nav.country' },
      { to: '/metadata/nationalities', labelKey: 'nav.nationality' },
      { to: '/metadata/police-stations', labelKey: 'nav.policeStation' },
      { to: '/metadata/letter-templates', labelKey: 'nav.letterTemplates' },
      { to: '/metadata/subsidiary-tasks', labelKey: 'nav.subsidiaryTasks' },
      { to: '/metadata/case-scenarios', labelKey: 'nav.caseScenarios' },
    ],
  },
];
